/**
 * A component for positioning an element in a Layer based off a targets position.
 */
import * as React from "react"
import styled from "@oui/system"
import useTether from "./useTether"
import { Edge, edges } from "./getPosition"
import getPreference, { Preference } from "./getPreference"
import Layer from "../Layer"
import { px } from "@oui/system/units"
import { omit } from "ramda"

type DefaultProps = typeof defaultProps
type Props = Readonly<
  {
    /**
     * A ref to the element used for positioning.
     */
    anchorRef: React.RefObject<HTMLElement>
    /**
     * Content to be rendered.
     */
    children: React.ReactElement
    /**
     * Preferred placement for the child element.
     */
    placement: Preference
  } & DefaultProps
>

type InternalProps = {
  children: React.ReactElement
  clientRect: ClientRect | undefined
} & Pick<React.HTMLAttributes<HTMLDivElement>, "children"> &
  React.RefAttributes<HTMLDivElement>

const defaultProps = {
  /**
   * The amount in pixels to shift the tethered element from the anchor.
   */
  offset: { top: 0, left: 0 }
}

const oppositeEdge = (edge: Edge) => {
  const newIndex = edges.indexOf(edge) + 2
  return edges[newIndex % edges.length]
}

const Tether__ = ({
  forwardedRef,
  ...rest
}: InternalProps & { forwardedRef: React.Ref<HTMLDivElement> }) => (
  <div {...omit(["clientRect"], rest)} ref={forwardedRef} />
)

const ForwardTether_ = (
  props: InternalProps,
  ref: React.Ref<HTMLDivElement>
) => <Tether__ {...props} forwardedRef={ref} />
ForwardTether_.displayName = "forwarded(Tether)"

const ForwardedTether = React.forwardRef(ForwardTether_)

const Tether_ = styled(ForwardedTether).attrs(
  ({ clientRect }: InternalProps) => ({
    // Using attrs for performance. Otherwise this component would inject 100's of
    // CSS classes into the DOM.
    style: clientRect
      ? {
          top: px(clientRect.top),
          left: px(clientRect.left)
        }
      : {
          top: 0,
          left: "-100vw"
        }
  })
)({
  position: "absolute"
})

const Tether = ({ anchorRef, children, offset, placement }: Props) => {
  const [primary] = getPreference(placement)
  const preferences = [placement, oppositeEdge(primary)] as [Edge, Edge]

  const elementRef = React.createRef<HTMLDivElement>()
  const [, clientRect] = useTether({
    anchorRef,
    elementRef,
    preferences,
    offset
  })
  return (
    <Layer>
      <Tether_ ref={elementRef} clientRect={clientRect}>
        {children}
      </Tether_>
    </Layer>
  )
}
Tether.defaultProps = defaultProps

export { Tether as _PropsComponent_ }
export default Tether
